/**
 * EXPERIMENT COPY — "Upgrade your plan", the wizard's first step.
 *
 * Production's version of this file owned the Monthly/Yearly toggle and the currency selector.
 * The toggle moved to the Screens step, where the price difference can be shown against a real
 * screen count. The currency select is back here, but as a passed-in slot rather than this step's
 * own state — see `currencySelect`.
 *
 * What is left is the card grid (see PlanCardGrid for the redesign) plus the Next button under
 * it. Next exists because the card body only MARKS a plan: someone who selects one and keeps
 * reading needs something telling them how to go on. A card's own CTA still advances directly,
 * as a shortcut for anyone who has already decided.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@scio3/ui';
import type { BillingInterval } from '@scio3/shared-types';
import { PlanCardGrid } from './PlanCardGrid';

type Props = {
  onPlanChange: (plan: string) => void;
  /** Card body click, and the Next button: select and move on. */
  onContinue: (plan?: string) => void;
  /** Marks the chosen card and enables Next. */
  selectedPlan?: string;
  currentPlan: string | undefined;
  /** Wording of the current-plan badge on the matching card. */
  isTrial?: boolean;
  currency: string;
  minPlanRequired?: string;
  hideFreePlanOption?: boolean;
  onSelectFree?: () => void;
  selectingFree?: boolean;
  /** True while the plan change is being validated server-side (downgrade warnings). */
  validating?: boolean;
  /**
   * The currency control, rendered directly above the grid and right-aligned to it.
   *
   * A slot rather than a prop pair, because the dialog owns the currency state and both variants
   * of the control (editable select / locked read-only text) — this step only owns WHERE it sits.
   * It sits here rather than in the wizard header because it labels the prices on the cards: up on
   * the title's line it was the furthest thing on screen from the numbers it applies to, and it
   * right-aligned to the header's own edge rather than the rightmost card's.
   */
  currencySelect?: ReactNode;
  /** The term the card prices are quoted for, and the setter behind the Monthly/Yearly toggle. */
  billingInterval: BillingInterval;
  onBillingIntervalChange: (interval: BillingInterval) => void;
};

export function WizardStepPlan({
  validating,
  currencySelect,
  billingInterval,
  onBillingIntervalChange,
  ...props
}: Props) {
  const { t } = useTranslation();

  return (
    <div className="mx-auto max-w-6xl pb-2" data-testid="wizard-step-plan">
      {/* Next is handed to the grid as its footer slot so it shares a line with the Free-plan
          bar instead of sitting on its own band below it. Disabled until a plan is marked — a
          Next that silently does nothing is worse than one that says why.
          It takes no alias: by the time this is clicked the card's `onPlanChange` has long since
          committed, so the wizard's own `selectedPlan` is correct. (The card CTAs DO pass one —
          they select and advance in the same click, before state settles.) */}
      <PlanCardGrid
        {...props}
        billingInterval={billingInterval}
        onBillingIntervalChange={onBillingIntervalChange}
        currencySelect={currencySelect}
        validating={validating}
        footerSlot={
          <Button
            onClick={() => props.onContinue()}
            disabled={!props.selectedPlan || validating}
            className="cursor-pointer px-8"
            data-testid="wizard-plan-next-btn"
          >
            {validating ? (
              <>
                <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />
                {t('Checking...')}
              </>
            ) : (
              t('Next')
            )}
          </Button>
        }
      />
    </div>
  );
}
